export class PollingAbortedError extends Error {
  constructor() {
    super("Polling aborted");
    this.name = "PollingAbortedError";
  }
}

export interface PollAsyncOptions<T> {
  intervalMs: number;
  maxAttempts?: number;
  shouldStopPolling?: (response: T, attemptNumber: number) => boolean;
  signal?: AbortSignal;
}

// Default behavior for API polling: stop as soon as the request returns a meaningful value.
const shouldStopPollingByDefault = <T>(response: T): boolean => response !== undefined && response !== null;

const throwIfPollingAborted = (signal?: AbortSignal): void => {
  if (signal?.aborted) {
    throw new PollingAbortedError();
  }
};

const waitForPollInterval = async (intervalMs: number, signal?: AbortSignal): Promise<void> =>
  new Promise((resolve, reject) => {
    let timeoutId = 0;

    const onAbort = (): void => {
      window.clearTimeout(timeoutId);
      reject(new PollingAbortedError());
    };

    timeoutId = window.setTimeout(() => {
      if (signal) {
        signal.removeEventListener("abort", onAbort);
      }

      resolve();
    }, intervalMs);

    if (signal) {
      signal.addEventListener("abort", onAbort, { once: true });
    }
  });

export const isPollingAbortedError = (error: unknown): error is PollingAbortedError =>
  error instanceof PollingAbortedError;

export const pollAsync = async <T>(
  request: () => Promise<T>,
  { intervalMs, maxAttempts, shouldStopPolling = shouldStopPollingByDefault, signal }: PollAsyncOptions<T>
): Promise<T> => {
  // Recursive polling keeps the control flow simple and avoids lint issues around await-in-loop.
  const pollAttempt = async (attemptNumber: number): Promise<T> => {
    throwIfPollingAborted(signal);

    const response = await request();

    throwIfPollingAborted(signal);

    if (shouldStopPolling(response, attemptNumber)) {
      return response;
    }

    if (maxAttempts !== undefined && attemptNumber >= maxAttempts) {
      return response;
    }

    // When maxAttempts is omitted, polling continues until the caller aborts it.
    await waitForPollInterval(intervalMs, signal);

    return pollAttempt(attemptNumber + 1);
  };

  return pollAttempt(1);
};
